import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  GraduationCap, 
  Briefcase, 
  Globe, 
  Rocket, 
  Users, 
  Heart,
  MapPin,
  Award,
  Lightbulb,
  Target
} from 'lucide-react';
import './About.css';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const founders = [
    {
      name: 'Arthur Dimeglio',
      role: 'Co-Founder & CTO',
      title: 'The Engineer',
      education: 'Master in International Business Management & Data Engineering and AI Conception',
      story: 'An entrepreneur since age 15, Arthur has navigated diverse industries from esports and fashion to cybersecurity and hospitality. His journey through multiple fields has shaped a unique perspective on technology and innovation.',
      expertise: ['AI & Data Engineering', 'International Business', 'Multi-Industry Experience', 'Strategic Vision'],
      industries: ['Esports', 'Graphism', 'Fashion', 'Arts', 'Cybersecurity', 'Hospitality', 'Medical', 'Technology'],
      icon: Rocket,
      gradient: 'linear-gradient(135deg, #d3c5aa 0%, #c4b593 100%)'
    },
    {
      name: 'Malek Moumtaz',
      role: 'Co-Founder & CEO',
      title: 'The Digital Strategist',
      education: 'Master in Digital Marketing',
      story: 'Malek reached out with a shared vision for technological innovation. His expertise in digital marketing combined with a passion for the Middle East market made him the perfect co-founder for our UAE venture.',
      expertise: ['Digital Marketing', 'Brand Strategy', 'Market Analysis', 'Customer Engagement'],
      industries: ['Digital Marketing', 'Brand Development', 'Customer Experience', 'Market Research'],
      icon: Target,
      gradient: 'linear-gradient(135deg, #c4b593 0%, #b8a082 100%)'
    }
  ];

  const companyValues = [
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'Pushing boundaries in AI and automation to create solutions that matter'
    },
    {
      icon: Globe,
      title: 'Global Vision',
      description: 'Bridging international expertise with Middle Eastern market opportunities'
    },
    {
      icon: Heart,
      title: 'Passion',
      description: 'United by our love for technology and commitment to excellence'
    },
    {
      icon: Users,
      title: 'Partnership',
      description: 'Building lasting relationships with clients and stakeholders'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        duration: 0.8
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <section className="about-section" ref={ref} id="about">
      <motion.div 
        className="about-container"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        {/* Hero Section */}
        <motion.div className="about-hero" variants={itemVariants}>
          <div className="hero-content">
            <motion.h1 className="about-title">
              <span className="title-line">OUR</span>
              <span className="title-line gradient-text">STORY</span>
            </motion.h1>
            <motion.p className="about-subtitle">
              From diverse backgrounds to unified vision – discover the journey that led to Astral AI
            </motion.p>
          </div>
          <motion.div className="hero-visual">
            <div className="location-badge">
              <MapPin size={20} />
              <span>Dubai, UAE</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Company Story */}
        <motion.div className="company-story" variants={itemVariants}>
          <div className="story-content">
            <h2 className="story-title">The Beginning</h2>
            <p className="story-text">
              Our journey began with a shared passion for technology and innovation. When Malek reached out with a vision 
              to create something extraordinary in the UAE, it sparked the beginning of Astral AI. United by our love for 
              cutting-edge technology and the dynamic opportunities in the Middle East, we decided to establish our company 
              in the heart of innovation – Dubai.
            </p>
            <p className="story-text">
              From our diverse educational backgrounds in business management, data engineering, AI conception, and digital 
              marketing, we bring a unique blend of technical expertise and strategic thinking to every project.
            </p>
          </div>
        </motion.div>

        {/* Founders Section */}
        <motion.div className="founders-section" variants={itemVariants}>
          <h2 className="section-title">Meet the Founders</h2>
          <div className="founders-grid">
            {founders.map((founder, index) => (
              <motion.div
                key={founder.name}
                className="founder-card"
                variants={itemVariants}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="founder-header">
                  <div className="founder-icon" style={{ background: founder.gradient }}>
                    <founder.icon size={32} color="white" />
                  </div>
                  <div className="founder-info">
                    <h3 className="founder-name">{founder.name}</h3>
                    <p className="founder-role">{founder.role}</p>
                    <p className="founder-title">{founder.title}</p>
                  </div>
                </div>
                
                <div className="founder-content">
                  <div className="education-section">
                    <div className="education-icon">
                      <GraduationCap size={20} />
                    </div>
                    <p className="education-text">{founder.education}</p>
                  </div>
                  
                  <p className="founder-story">{founder.story}</p>
                  
                  <div className="expertise-section">
                    <h4 className="expertise-title">Expertise</h4>
                    <div className="expertise-tags">
                      {founder.expertise.map((skill, skillIndex) => (
                        <span key={skillIndex} className="expertise-tag">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="industries-section">
                    <h4 className="industries-title">Industry Experience</h4>
                    <div className="industries-grid">
                      {founder.industries.map((industry, industryIndex) => (
                        <span key={industryIndex} className="industry-pill">
                          {industry}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Values Section */}
        <motion.div className="values-section" variants={itemVariants}>
          <h2 className="section-title">Our Values</h2>
          <div className="values-grid">
            {companyValues.map((value, index) => (
              <motion.div
                key={value.title}
                className="value-card"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <div className="value-icon">
                  <value.icon size={32} />
                </div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Vision Section */}
        <motion.div className="vision-section" variants={itemVariants}>
          <div className="vision-content">
            <h2 className="vision-title">Our Vision</h2>
            <p className="vision-text">
              To bridge the gap between cutting-edge AI technology and real-world business solutions, 
              creating intelligent systems that empower enterprises and enrich lives. We envision a future 
              where technology serves humanity with precision, creativity, and purpose.
            </p>
            <div className="vision-stats">
              <div className="stat-item">
                <Award size={24} />
                <div className="stat-content">
                  <span className="stat-number">2024</span>
                  <span className="stat-label">Founded</span>
                </div>
              </div>
              <div className="stat-item">
                <Briefcase size={24} />
                <div className="stat-content">
                  <span className="stat-number">10+</span>
                  <span className="stat-label">Industries</span>
                </div>
              </div>
              <div className="stat-item">
                <Globe size={24} />
                <div className="stat-content">
                  <span className="stat-number">UAE</span>
                  <span className="stat-label">Based</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default About; 